import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { nutritionAPI } from '../../services/api'
import { CheckCircleIcon, TrashIcon, PencilIcon, ClockIcon } from '@heroicons/react/24/outline'
import MacroBadge from '../shared/MacroBadge'

/**
 * SavedMealEditCard Component
 *
 * Displays a confirmation card after a meal is auto-saved.
 * Allows quick editing of meal type and deletion.
 */
export default function SavedMealEditCard({
  savedMeal,
  analysisResult,
  onDismiss,
  onProcessNext,
  batchInfo
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [isEditingMealType, setIsEditingMealType] = useState(false)
  const [selectedMealType, setSelectedMealType] = useState(savedMeal?.meal_type || analysisResult?.suggested_meal_type || 'snack')

  // Update meal type mutation
  const updateMutation = useMutation({
    mutationFn: (data) => nutritionAPI.updateMeal(savedMeal?.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meals'] })
      queryClient.invalidateQueries({ queryKey: ['foodLogs'] })
      queryClient.invalidateQueries({ queryKey: ['weeklySummary'] })
      setIsEditingMealType(false)
    },
    onError: (error) => {
      console.error('Failed to update meal:', error)
      alert(error.response?.data?.detail || t('nutrition.errors.updateMealFailed'))
    }
  })

  // Delete meal mutation
  const deleteMutation = useMutation({
    mutationFn: () => nutritionAPI.deleteMeal(savedMeal?.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meals'] })
      queryClient.invalidateQueries({ queryKey: ['foodLogs'] })
      queryClient.invalidateQueries({ queryKey: ['weeklySummary'] })
      onDismiss?.()
    },
    onError: (error) => {
      console.error('Failed to delete meal:', error)
      alert(error.response?.data?.detail || t('nutrition.errors.deleteMealFailed'))
    }
  })

  const handleMealTypeChange = (newType) => {
    setSelectedMealType(newType)
    if (savedMeal?.id) {
      updateMutation.mutate({ meal_type: newType })
    }
  }

  const mealTypeOptions = [
    { value: 'breakfast', label: t('nutrition.mealTypes.breakfast', 'Breakfast'), icon: '🌅' },
    { value: 'lunch', label: t('nutrition.mealTypes.lunch', 'Lunch'), icon: '🌞' },
    { value: 'dinner', label: t('nutrition.mealTypes.dinner', 'Dinner'), icon: '🌙' },
    { value: 'snack', label: t('nutrition.mealTypes.snack', 'Snack'), icon: '🍎' }
  ]

  const currentMealOption = mealTypeOptions.find(opt => opt.value === selectedMealType.toLowerCase()) || mealTypeOptions.find(opt => opt.value === 'snack')

  // Get meal totals from analysis result
  const mealTotal = analysisResult?.meal_total || {}
  const foodsCount = analysisResult?.foods_identified?.length || 0

  return (
    <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 space-y-4">
      {/* Success Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <CheckCircleIcon className="h-6 w-6 text-green-600" />
          <span className="font-medium text-green-800">
            {t('nutrition.autoSave.savedSuccessfully', 'Meal saved successfully!')}
          </span>
        </div>
        {batchInfo?.hasMore && (
          <span className="text-sm text-green-700 bg-green-100 px-2 py-1 rounded">
            {batchInfo.currentIndex} / {batchInfo.totalFiles}
          </span>
        )}
      </div>

      {/* Meal Summary Card */}
      <div className="bg-surface-container rounded-lg p-4 shadow-sm">
        <div className="flex items-start justify-between">
          {/* Left: Food info */}
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-2xl">{currentMealOption.icon}</span>
              {isEditingMealType ? (
                <select
                  value={selectedMealType.toLowerCase()}
                  onChange={(e) => handleMealTypeChange(e.target.value)}
                  disabled={updateMutation.isPending}
                  className="form-select text-sm"
                  autoFocus
                  onBlur={() => setIsEditingMealType(false)}
                >
                  {mealTypeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.icon} {opt.label}
                    </option>
                  ))}
                </select>
              ) : (
                <button
                  onClick={() => setIsEditingMealType(true)}
                  className="flex items-center text-lg font-semibold text-gray-900 hover:text-indigo-600"
                >
                  {currentMealOption.label}
                  <PencilIcon className="h-4 w-4 ml-1 text-gray-400" />
                </button>
              )}
            </div>

            <p className="text-sm text-gray-600">
              {t('nutrition.autoSave.itemsLogged', { count: foodsCount })}
            </p>

            {/* Nutrition summary */}
            <div className="mt-2">
              <MacroBadge
                calories={mealTotal.calories}
                protein={mealTotal.protein_g}
                carbs={mealTotal.carbohydrates_g}
                fat={mealTotal.fat_g}
                compact
              />
            </div>
          </div>

          {/* Right: Delete button */}
          <button
            onClick={() => {
              if (confirm(t('nutrition.autoSave.confirmDelete', 'Delete this meal?'))) {
                deleteMutation.mutate()
              }
            }}
            disabled={deleteMutation.isPending}
            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            title={t('common.delete', 'Delete')}
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Timestamp source indicator */}
        <div className="flex items-center mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
          <ClockIcon className="h-4 w-4 mr-1" />
          {analysisResult?.timestamp_source === 'exif'
            ? t('nutrition.autoSave.timestampFromPhoto', 'Time detected from photo')
            : t('nutrition.autoSave.timestampCurrent', 'Using current time')
          }
          {analysisResult?.suggested_date && (
            <span className="ml-2">
              ({analysisResult.suggested_date})
            </span>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex space-x-3">
        {batchInfo?.hasMore ? (
          <button
            onClick={onProcessNext}
            className="flex-1 btn-primary"
          >
            {t('nutrition.autoSave.nextPhoto', 'Next Photo')} ({batchInfo.currentIndex + 1}/{batchInfo.totalFiles})
          </button>
        ) : (
          <button
            onClick={onDismiss}
            className="flex-1 btn-primary"
          >
            {t('nutrition.autoSave.done', 'Done')}
          </button>
        )}
      </div>
    </div>
  )
}
